import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { TypeAnimation } from "react-type-animation";
import { motion } from "framer-motion";
import {
  FaShieldAlt,
  FaChartLine,
  FaUniversity,
  FaArrowRight,
  FaBrain,
  FaUsers,
  FaComments,
} from "react-icons/fa";
import { SignUpCTA } from "../components/SignUpCTA";
import { useNavigate } from "react-router-dom";
import logo from "../core-assets/images/logo.png";
import { ReactComponent as FirebaseLogo } from "../core-assets/images/firebase-logo.svg";
import { ReactComponent as TursoLogo } from "../core-assets/images/turso-logo.svg";

function HomePage() {
  const navigate = useNavigate();

  const features = [
    {
      icon: FaBrain,
      title: "Smart Account Syncing",
      description:
        "Connect all your family's accounts in seconds. Our AI automatically categorizes and tracks every transaction across all institutions.",
      color: "indigo",
    },
    {
      icon: FaShieldAlt,
      title: "Bank-Level Security",
      description:
        "Enterprise-grade encryption with SOC2 compliance. Your credentials are never stored and all data is protected with AES-256 encryption.",
      color: "blue",
    },
    {
      icon: FaChartLine,
      title: "Real-Time Tracking",
      description:
        "See your complete financial picture instantly. Get live balance updates and transaction alerts across all connected accounts.",
      color: "green",
    },
    {
      icon: FaUniversity,
      title: "Major Financial Institutions",
      description:
        "Connect with over 11,000 banks across US, Canada, UK and Australia. Support for checking, savings, credit cards and investments.",
      color: "purple",
    },
    {
      icon: FaUsers,
      title: "Family Access",
      description:
        "Share financial visibility with your spouse while maintaining privacy. Each family member gets their own secure dashboard access.",
      color: "pink",
    },
    {
      icon: FaComments,
      title: "AI Transaction Assistant",
      description:
        "Chat naturally with your transaction history. Get instant insights about spending patterns and financial recommendations.",
      color: "orange",
    },
  ];

  return (
    <>
      <Helmet>
        <title>OmniButler - Simplify Your Family&#39;s Finances</title>
      </Helmet>

      {/* Enhanced gradient orbs with more subtle animation */}
      <span className="fixed blur-[200px] size-[600px] rounded-full top-[10%] start-[10%] bg-indigo-600/10 animate-pulse pointer-events-none -z-10"></span>
      <span className="fixed blur-[200px] size-[600px] rounded-full bottom-[10%] end-[10%] bg-blue-600/10 animate-pulse pointer-events-none -z-10"></span>

      {/* Hero Section */}
      <section className="relative w-full pt-32 pb-32 bg-transparent overflow-hidden">
        <div className="container relative mx-auto px-6 lg:px-12 xl:px-0 max-w-screen-2xl">
          {/* Purple Background Shape */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-b from-indigo-500 via-purple-600 to-pink-600 opacity-20 transform rotate-45"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.2 }}
            transition={{ duration: 1.2 }}
          />

          <div className="flex flex-col-reverse lg:flex-row items-center justify-center max-w-8xl mx-auto xl:pl-36">
            {/* Left Column - Text Content */}
            <div className="w-full lg:w-1/2 mt-12 lg:mt-0 text-center lg:text-left lg:pl-24 lg:pr-12 xl:pl-12">
              <h1 className="text-5xl sm:text-6xl lg:text-6xl xl:text-7xl font-bold text-gray-800 dark:text-white leading-tight whitespace-nowrap">
                Your Family&#39;s Wealth,
                <br />
                <TypeAnimation
                  sequence={[
                    "Secured",
                    2000,
                    "Simplified",
                    2000,
                    "Clear",
                    2000,
                  ]}
                  wrapper="span"
                  speed={50}
                  className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-transparent bg-clip-text"
                  repeat={Infinity}
                />
              </h1>
              <p className="mt-6 text-xl sm:text-2xl lg:text-lg text-gray-700 dark:text-gray-300 max-w-xl mx-auto lg:mx-0">
                One powerful dashboard for all your family&#39;s accounts. Get
                clarity on your total wealth in seconds.
              </p>
              <div className="mt-8">
                <SignUpCTA onClick={() => navigate("/login")} size="large">
                  Join Private Beta
                </SignUpCTA>
              </div>
            </div>

            {/* Right Column - Logo Image */}
            <div className="w-full lg:w-1/2 flex justify-center mb-12 lg:mb-0">
              <div className="w-full max-w-2xl flex justify-center">
                <motion.img
                  src={logo}
                  alt="OmniButler Logo"
                  className="w-full max-w-sm"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1 }}
                />
              </div>
            </div>
          </div>

          {/* Total Net Worth Section */}
          <div className="container mx-auto px-6 mt-16 relative z-10">
            <div className="max-w-2xl mx-auto bg-white dark:bg-slate-900 shadow-lg rounded-xl p-8 relative">
              <div className="absolute inset-0 bg-white dark:bg-slate-900 rounded-xl" />
              <div className="relative">
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-6 text-center">
                  Connect your accounts once, and always see your complete
                  financial picture
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex flex-col items-center">
                    <h3 className="text-2xl font-bold">Total Net Worth</h3>
                    <span className="text-3xl font-bold text-indigo-600 mt-2">
                      $2.4M
                    </span>
                  </div>
                  <div className="space-y-4">
                    {["Chase", "Bank of America", "Fidelity"].map((bank) => (
                      <div
                        key={bank}
                        className="flex justify-between items-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg"
                      >
                        <span className="font-medium">{bank}</span>
                        <span className="text-green-600 font-medium">
                          Connected
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-6 text-center">
                  <div className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                    Get early access to the future of family finance
                  </div>
                  <SignUpCTA onClick={() => navigate("/login")} size="regular">
                    Join Private Beta
                  </SignUpCTA>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="py-12 bg-white dark:bg-slate-900">
        <div className="container">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="text-center p-6 rounded-xl transition-all duration-300 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                <feature.icon
                  className={`w-12 h-12 mx-auto mb-4 text-${feature.color}-600`}
                />
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-slate-600 dark:text-slate-300">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50 dark:bg-slate-800">
        <div className="container">
          <h2 className="text-3xl font-bold mb-12 text-center">
            Common Questions
          </h2>
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {[
              {
                q: "How secure is my financial data?",
                a: "We use bank-level encryption and never store your credentials. All data is encrypted using AES-256, the same security standard used by major financial institutions.",
                icon: FaShieldAlt,
                color: "indigo",
              },
              {
                q: "Which banks do you support?",
                a: "We support major banks across the US, Canada, UK, and Australia through our partnership with Plaid, including Chase, RBC, Barclays, Commonwealth Bank, and thousands more.",
                icon: FaUniversity,
                color: "blue",
              },
              {
                q: "Can I add my spouse to the account?",
                a: "Yes! Your spouse gets their own secure login while sharing access to your consolidated financial dashboard.",
                icon: FaUsers,
                color: "green",
              },
              {
                q: "How long does setup take?",
                a: "Most families connect their accounts in under 2 minutes. Simply sign in with Google and connect your banks through our secure Plaid integration.",
                icon: FaBrain,
                color: "purple",
              },
            ].map((faq, index) => (
              <motion.div
                key={faq.q}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="group bg-white dark:bg-slate-900 rounded-xl p-6 shadow-sm hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center space-x-4 mb-4">
                  <faq.icon
                    className={`w-6 h-6 text-${faq.color}-600 transition-transform duration-300 group-hover:scale-110`}
                  />
                  <h3 className="font-semibold text-lg">{faq.q}</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-400 pl-10">
                  {faq.a}
                </p>
              </motion.div>
            ))}
          </div>
          <div className="text-center">
            <Link
              to="/faq"
              className="inline-flex items-center text-indigo-600 hover:text-indigo-700 font-medium transition-colors duration-300 group"
              style={{ zIndex: 10 }}
            >
              View all FAQs{" "}
              <FaArrowRight className="ml-2 text-sm transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* Security Partners Section */}
      <section className="py-20 bg-gray-100 dark:bg-slate-700">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">
              Enterprise-Grade Security
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Protected by industry leaders in security and data privacy
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-20 items-center justify-items-center max-w-6xl mx-auto">
            {[
              {
                Component: FirebaseLogo,
                alt: "Firebase Auth",
                className: "w-32 sm:w-40 lg:w-44",
              },
              {
                src: require("../core-assets/images/plaid-logo.png"),
                alt: "Plaid",
                className: "w-32 sm:w-40 lg:w-44",
              },
              {
                src: require("../core-assets/images/google-cloud-logo.png"),
                alt: "Google Cloud",
                className: "w-32 sm:w-40 lg:w-44",
              },
              {
                Component: TursoLogo,
                alt: "Turso Database",
                className: "w-32 sm:w-40 lg:w-44",
              },
            ].map((logo) => (
              <motion.div key={logo.alt}>
                {logo.Component ? (
                  <logo.Component
                    className={`${logo.className} opacity-75 hover:opacity-100 transition-all duration-300`}
                  />
                ) : (
                  <img
                    src={logo.src}
                    alt={logo.alt}
                    className={`${logo.className} opacity-75 hover:opacity-100 transition-all duration-300`}
                  />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default HomePage;
